// Simple RFC 4180-style CSV parser (handles quoted fields, escaped quotes, CRLF).
// Returns { headers: string[], rows: Record<string,string>[] }.
export function parseCsv(text) {
  if (!text) return { headers: [], rows: [] };
  // Strip UTF-8 BOM if present
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);

  const records = [];
  let field = '';
  let record = [];
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else { inQuotes = false; }
      } else {
        field += c;
      }
    } else {
      if (c === '"') {
        inQuotes = true;
      } else if (c === ',') {
        record.push(field); field = '';
      } else if (c === '\n' || c === '\r') {
        if (c === '\r' && text[i + 1] === '\n') i++;
        record.push(field); field = '';
        records.push(record); record = [];
      } else {
        field += c;
      }
    }
  }
  if (field.length > 0 || record.length > 0) {
    record.push(field);
    records.push(record);
  }

  // Drop fully empty trailing rows
  const cleaned = records.filter(r => r.some(v => (v || '').trim() !== ''));
  if (cleaned.length === 0) return { headers: [], rows: [] };

  const headers = cleaned[0].map(h => (h || '').trim());
  const rows = cleaned.slice(1).map(r => {
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = (r[idx] ?? '').trim(); });
    return obj;
  });
  return { headers, rows };
}

// Common bank-statement column names, mapped to our canonical fields.
const FIELD_HINTS = {
  date: ['date', 'posted', 'posting date', 'posted date', 'transaction date', 'trans date'],
  description: ['description', 'memo', 'payee', 'details', 'name', 'merchant', 'narrative'],
  amount: ['amount', 'transaction amount'],
  debit: ['debit', 'withdrawal', 'withdrawals', 'money out', 'payment', 'outflow'],
  credit: ['credit', 'deposit', 'deposits', 'money in', 'inflow'],
  type: ['type', 'transaction type', 'dr/cr']
};

// Given a header array, guess which columns map to our fields.
export function guessColumnMapping(headers) {
  const lower = headers.map(h => (h || '').toLowerCase().trim());
  const find = (candidates) => {
    for (const cand of candidates) {
      const idx = lower.indexOf(cand);
      if (idx !== -1) return headers[idx];
    }
    // Substring fallback
    for (let i = 0; i < lower.length; i++) {
      if (candidates.some(c => lower[i].includes(c))) return headers[i];
    }
    return '';
  };
  return {
    date: find(FIELD_HINTS.date),
    description: find(FIELD_HINTS.description),
    amount: find(FIELD_HINTS.amount),
    debit: find(FIELD_HINTS.debit),
    credit: find(FIELD_HINTS.credit),
    type: find(FIELD_HINTS.type)
  };
}

// Parse a number string that may have $, commas, parens for negatives.
export function parseAmount(raw) {
  if (raw == null) return NaN;
  let s = String(raw).trim();
  if (!s) return NaN;
  let negative = false;
  if (s.startsWith('(') && s.endsWith(')')) { negative = true; s = s.slice(1, -1); }
  s = s.replace(/[$,\s]/g, '');
  if (s.startsWith('-')) { negative = !negative; s = s.slice(1); }
  const n = parseFloat(s);
  if (isNaN(n)) return NaN;
  return negative ? -n : n;
}

// Parse a date string into YYYY-MM-DD. Accepts common US formats.
export function parseDateToIso(raw) {
  if (!raw) return '';
  const s = String(raw).trim();
  if (!s) return '';
  // Already ISO
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  // MM/DD/YYYY or M/D/YY (prefer 4-digit year)
  const mdy = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4}|\d{2})/);
  if (mdy) {
    let [, m, d, y] = mdy;
    if (y.length === 2) y = (parseInt(y, 10) >= 70 ? '19' : '20') + y;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  return '';
}

// Map parsed CSV rows + user column mapping into normalized transaction drafts.
// kind is 'expense' (money out) or 'deposit' (money in).
// Skips rows that lack a valid date or amount.
export function rowsToDrafts(rows, mapping) {
  const drafts = [];
  rows.forEach((row, idx) => {
    const rawDate = mapping.date ? row[mapping.date] : '';
    const date = parseDateToIso(rawDate);
    const description = (mapping.description ? row[mapping.description] : '') || '';

    let amount = NaN;
    let kind = null;

    if (mapping.debit || mapping.credit) {
      const debit = mapping.debit ? parseAmount(row[mapping.debit]) : NaN;
      const credit = mapping.credit ? parseAmount(row[mapping.credit]) : NaN;
      if (!isNaN(debit) && debit !== 0) { amount = Math.abs(debit); kind = 'expense'; }
      else if (!isNaN(credit) && credit !== 0) { amount = Math.abs(credit); kind = 'deposit'; }
    } else if (mapping.amount) {
      const a = parseAmount(row[mapping.amount]);
      if (!isNaN(a) && a !== 0) {
        // Optional type column overrides sign
        const typeVal = (mapping.type ? row[mapping.type] : '').toLowerCase();
        if (typeVal.includes('debit') || typeVal.includes('withdraw') || typeVal === 'dr') {
          kind = 'expense';
        } else if (typeVal.includes('credit') || typeVal.includes('deposit') || typeVal === 'cr') {
          kind = 'deposit';
        } else {
          kind = a < 0 ? 'expense' : 'deposit';
        }
        amount = Math.abs(a);
      }
    }

    if (!date || isNaN(amount) || amount === 0 || !kind) {
      drafts.push({
        rowIndex: idx,
        valid: false,
        reason: !date ? 'Missing/invalid date' : (!amount || isNaN(amount)) ? 'Missing amount' : 'Unrecognized row',
        raw: row
      });
      return;
    }

    drafts.push({
      rowIndex: idx,
      valid: true,
      kind,
      date,
      description: description || '(no description)',
      amount,
      raw: row
    });
  });
  return drafts;
}
